import { useMemo, useState } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';
import { client } from '~/client';
import { CreateJobForm } from '~/components/job/CreateJobForm';
import {
  getBulkAssetLocationId,
  readBulkAssetJobCreation,
} from '~/helpers/bulkAssetJobCreation';
import { graphql } from '~/gql';
import { CreateJobFormQuery } from '~/gql/graphql';

const CreateJobFormDocument = graphql(`
  query CreateJobForm {
    integrations
    attributes(entityType: "Site") {
      id
      type
      category
      name
      value
      condition
    }
    jobNameOptions
    jobTemplates(status: Active) {
      ...JobTemplateFields
    }
    locations(status: Active) {
      __typename
      id
      name
      ... on Asset {
        deployment {
          id
          displayLocation
          defaultSourceId
        }
      }
    }
    contacts(status: Active) {
      ...ContactFields
    }
  }
`);

export async function loader(): Promise<CreateJobFormQuery> {
  const { data, error } = await client
    .query(CreateJobFormDocument, {}, { requestPolicy: 'cache-first' })
    .toPromise();

  if (error || !data) {
    throw new Response(error?.message, { status: 500 });
  }

  return data;
}

export default function CreateJobFromBulkAssetsRoute() {
  const data = useLoaderData() as Awaited<ReturnType<typeof loader>>;
  const navigate = useNavigate();

  // Read bulk asset data from sessionStorage to extract locationId
  // This must be done BEFORE CreateJobForm reads it (which clears it)
  // Using useState with lazy initializer to ensure parsing only runs once (even in Strict Mode)
  const [bulkAssetData] = useState(() => readBulkAssetJobCreation());

  // Extract locationId from bulk assets (should be single location since we validated)
  const locationId = useMemo(
    () => getBulkAssetLocationId(bulkAssetData?.assets),
    [bulkAssetData]
  );

  return (
    <CreateJobForm
      data={data}
      animationSource='asset'
      locationId={locationId}
      onClose={() => {
        // Navigate back to assets list (outlet will close, selection preserved)
        navigate('/assets', { replace: true });
      }}
      onSuccess={(data) => {
        // Navigate back to assets list, optionally highlighting the created job
        // Clear selection after successful job creation
        const jobId = data?.createJobs?.[0]?.id;
        const targetUrl = jobId ? `/assets?highlightJob=${jobId}` : '/assets';
        navigate(targetUrl, {
          replace: true,
          state: {
            jobCreated: true, // Flag to indicate job was created successfully, so selection should be cleared
          },
        });
        // Note: sessionStorage is already cleared by CreateJobForm when initializing tasks
      }}
    />
  );
}
